import React, { useState, useMemo } from "react";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, ReferenceLine } from "recharts";
import { Card, SectionLabel } from "./ui-bits";
import { usePoll } from "../hooks/usePoll";

const GREEN = "#00FF88";
const RED = "#FF453A";
const GREY = "#71717A";

function chartColor(current, prev) {
  if (!prev || !current) return GREY;
  const diff = current - prev;
  const eps = Math.max(prev * 0.0001, 0.01);
  if (diff > eps) return GREEN;
  if (diff < -eps) return RED;
  return GREY;
}

const CHART_MARGIN = { top: 6, right: 12, left: 0, bottom: 0 };
const PREV_LINE_STROKE = "#8A8A8A";
const TOOLTIP_CONTENT_STYLE = {
  background: "#121212", border: "1px solid #272727", borderRadius: 2,
  fontFamily: "Inter, system-ui, sans-serif", fontSize: 11, color: "#F3F4F6",
};

const DEFAULT_RANGES = ["1D", "1W", "1M", "3M", "YTD"];

/**
 * Reusable price chart. Fetches history via `fetchHistory(symbol, range)` and renders the same
 * Bloomberg-style area chart used on the Dashboard.
 */
export const PriceChart = ({
  symbol,
  label,
  ranges = DEFAULT_RANGES,
  fetchHistory,
  className = "",
  cardTitle = "MARKET SNAPSHOT",
}) => {
  const [range, setRange] = useState(ranges[0]);
  const { data, loading } = usePoll(
    () => fetchHistory(symbol, range),
    range === "1D" ? 20_000 : 5 * 60_000,
    [symbol, range],
  );

  const series = data?.series || [];
  const prev = data?.prev_close || 0;
  const current = data?.current || (series.at?.(-1)?.v ?? 0);
  const color = chartColor(current, prev);
  const positive = color !== RED;
  const change = current && prev ? current - prev : 0;
  const pct = prev ? (change / prev) * 100 : 0;
  const gradKey = color === GREEN ? "g" : color === RED ? "r" : "n";

  // Always include prev-close in the Y-axis domain so the reference line is visible.
  const yDomain = useMemo(
    () => (prev > 0
      ? [(dataMin) => Math.min(dataMin, prev), (dataMax) => Math.max(dataMax, prev)]
      : ["dataMin", "dataMax"]),
    [prev]
  );

  const tooltipCursor = useMemo(() => ({ stroke: color, strokeOpacity: 0.5, strokeDasharray: "2 2" }), [color]);
  const tooltipLabelFormatter = useMemo(() => (l) => `${range === "1D" ? "IST " : ""}${l}`, [range]);
  const tooltipValueFormatter = useMemo(
    () => (v) => {
      const diff = v - prev;
      const dpct = prev ? (diff / prev) * 100 : 0;
      return [
        `${v.toFixed(2)}  Δ ${diff >= 0 ? "+" : ""}${diff.toFixed(2)} (${dpct >= 0 ? "+" : ""}${dpct.toFixed(2)}%)`,
        symbol,
      ];
    },
    [prev, symbol]
  );

  return (
    <Card className={className} data-testid={`price-chart-${symbol}`}>
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <SectionLabel>{cardTitle}</SectionLabel>
          <div className="mt-1 flex items-baseline gap-3 flex-wrap">
            <span className="fij-mono text-2xl sm:text-3xl text-fij-text" data-testid="price-chart-value">
              {current ? current.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
            </span>
            <span className="font-mono text-sm" style={{ color }} data-testid="price-chart-change">
              {change >= 0 ? "+" : ""}{change.toFixed(2)} ({pct >= 0 ? "+" : ""}{pct.toFixed(2)}%)
            </span>
          </div>
          <div className="font-mono text-[10px] text-fij-textMuted mt-1">
            {label || symbol} · {range} · Prev Close: {prev ? prev.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
          </div>
        </div>
        <div className="flex items-center gap-1 font-mono text-[10px] flex-wrap">
          {ranges.map((r) => (
            <button
              key={r}
              data-testid={`range-${r}`}
              onClick={() => setRange(r)}
              className={`px-2 py-1 rounded-sm border transition-colors ${
                r === range
                  ? "bg-fij-green/10 border-fij-green/40 text-fij-green"
                  : "border-fij-border text-fij-textDim hover:text-fij-green hover:border-fij-green/40"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      <div className="h-56 sm:h-64 -mx-2 relative">
        {loading && !series.length && (
          <div className="absolute inset-0 grid place-items-center font-mono text-xs text-fij-textMuted">
            Loading {range}…
          </div>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={series} margin={CHART_MARGIN}>
            <defs>
              <linearGradient id={`grad-${gradKey}-${symbol}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.32} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="label" hide />
            <YAxis hide domain={yDomain} />
            <Tooltip
              contentStyle={TOOLTIP_CONTENT_STYLE}
              cursor={tooltipCursor}
              labelFormatter={tooltipLabelFormatter}
              formatter={tooltipValueFormatter}
            />
            <Area
              type="monotone"
              dataKey="v"
              stroke={color}
              strokeWidth={1.6}
              fill={`url(#grad-${gradKey}-${symbol})`}
              isAnimationActive={false}
            />
            {/* Prev-close reference line drawn AFTER Area so it renders on top. */}
            {prev > 0 && (
              <ReferenceLine
                y={prev}
                stroke={PREV_LINE_STROKE}
                strokeOpacity={0.6}
                strokeDasharray="4 4"
                strokeWidth={1}
                ifOverflow="extendDomain"
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

export default PriceChart;
